/**
 * BONE WATER — LIQUID BUTTONS
 * assets/bw-liquid-btn.js
 *
 * Puts a flat pool of liquid inside a button. It drifts on its own, under a
 * pointer it is pulled up toward the cursor and settles back afterwards, and
 * a press fills the capsule in a rush before it drains back to its level.
 *
 * Opt-in per button: any element carrying data-bw-liquid is picked up and
 * nothing else on the page is touched. Colours come from the element's own
 * custom properties (--bw-liquid, --bw-liquid-ink), so the section decides
 * them in Liquid and this file never names a brand colour.
 *
 * WHY A SIMULATION AND NOT KEYFRAMES
 * The surface is a row of 26 sprung columns coupled to their neighbours — a
 * one-dimensional pool. A pull at one point therefore travels outward and
 * reflects off the ends, so no two hovers produce the same shape and the
 * return is damping rather than an animation played backwards. The idle drift
 * is the same columns driven by two sine waves whose periods do not divide
 * into each other, which is what stops it visibly looping.
 *
 * WHAT IT COSTS
 * The maths is nothing (26 columns, a few hundred flops a frame). The cost is
 * re-rasterising the button, so that is what gets rationed:
 *   - offscreen buttons are not stepped at all (IntersectionObserver)
 *   - an idle button repaints at 20fps, not 60 — its motion is slow enough
 *     that the difference cannot be seen
 *   - only the button under the pointer runs at full rate
 *   - the label copy is only repainted while liquid actually reaches it
 *   - paint containment keeps the invalidation inside the button box
 * Reduced motion gets one static pour and no loop at all.
 *
 * Presentation lives in bw-tokens.css (.bw-liquid, .bw-liquid__ghost).
 * Requires: assets/bw-tokens.css
 */
(function () {
  /* Emitted by every section that owns a liquid button, so it can arrive more
     than once on a page. Guard rather than fight it — a second copy would
     mount a second pool inside each button. */
  if (window.__bwLiquidBound) return;
  window.__bwLiquidBound = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* Columns across the surface. 26 puts a control point every ~10px on a
     260px button, which is finer than the smallest ripple the spread pass can
     carry — more points would cost paint without adding shape. */
  var COLS = 26;

  /* The sim always advances in fixed 1/60 steps regardless of how often we
     paint. Variable dt in a spring system changes its stiffness with the
     frame rate, which is how one pull ends up feeling different on a 120Hz
     laptop and a throttled phone. */
  var STEP = 1 / 60;
  var MAX_STEPS = 4; /* after a stall, catch up four steps and drop the rest */

  var REST_K = 26; /* pull back toward the resting line */
  var DAMP = 3.1; /* velocity decay — low enough to leave ripples behind */
  var SPREAD = 0.24; /* how strongly a column drags its neighbours */
  var PULL_K = 30; /* how hard the cursor lifts the surface under it */
  /* 20fps while nobody is hovering — and half that on a touch screen.

     A coarse pointer cannot hover, so on a phone this loop only ever has two
     jobs: the slow drift, and the fill on press. The press is driven at the
     full frame rate because it is the thing the finger is waiting on; the
     drift is a millimetre of movement a second and reads identically at 10fps.
     Halving it halves the repaint cost on exactly the devices measuring worst.

     Read once. A device that gains a mouse mid-session keeps the rate it
     booted with, which is a fair trade for not re-reading a media query inside
     the frame loop. */
  var IDLE_MS = fine.matches ? 50 : 100;

  /* The press. On pointerdown (mouse, pen or finger — this one is not
     hover-gated) the surface is driven straight up to the brim over a few
     frames and held there for FILL_S, then released to the resting spring,
     which drains it back in the usual damped return. A link that navigates
     never gets to the drain, which is the point: the fill is the button
     acknowledging the tap before the page goes. */
  var FILL_S = 0.5; /* seconds held at the brim */
  var FILL_RATE = 0.28; /* fraction of the remaining rise taken per step */

  var pools = [];
  var running = false;
  var last = 0;
  /* Set while a lead dialog is open. Everything these buttons do is invisible
     behind a blurred backdrop, and repainting one is what forces that blur to
     be recomputed across the whole viewport — so the loop stops entirely
     rather than merely being throttled. */
  var parked = false;

  /* THE IDLE POUR WAITS FOR THE PAGE TO FINISH LOADING.

     This loop is cheap per frame but it is never free, and while the page is
     still loading it competes with the one thing that matters more: getting the
     hero painted. Measured on the live home page against a throttled Moto G,
     this file was attributed 2,136ms of main thread — of which only 84ms was
     actually running JavaScript. The rest was the layout and paint the loop
     asks for, sixty times a second, before the page had even finished arriving.

     So nothing pours until the page is settled. What "settled" means, first of:

       - window load, then an idle callback. The loop starts in whatever gap the
         browser has left once it has finished its own work.
       - the visitor touching anything. Interaction beats the timer outright: a
         button pressed during the wait must fill on that press, not after it.

     Held in a separate flag from `parked` on purpose. parked is a state the
     page returns to every time a dialog opens; this happens once. */
  var warming = true;

  function settle() {
    if (!warming) return;
    warming = false;
    start();
  }

  function rand(a, b) { return a + Math.random() * (b - a); }
  function clamp(n, lo, hi) { return n < lo ? lo : n > hi ? hi : n; }
  /* A missing or unparseable attribute falls back; a parsed 0 does not. */
  function num(v, dflt) { var n = parseFloat(v); return isNaN(n) ? dflt : n; }

  /* ---------------------------------------------------------------------- */

  function Pool(el) {
    this.el = el;
    this.w = 0;
    this.h = 0;
    this.visible = false;
    this.hidden = false;
    this.hover = false;
    this.px = 0; /* pointer, in the button's own pixels */
    this.py = 0;
    this.t = rand(0, 1000); /* every button starts elsewhere in the drift */
    this.acc = 0;
    this.painted = 0;
    this.ghostLit = true;
    this.fillT = 0; /* seconds of press-fill still to hold */

    /* Resting depth and drift amplitude as fractions of the button height, so
       a small button and a tall one hold the same-looking pour. */
    /* num(), not `|| default`: every one of these settings offers 0 as a
       meaningful value — a drift of 0 is documented in the editor as "holds it
       flat until a cursor arrives" — and 0 is falsy, so the || form silently
       replaced it with the default and the setting did nothing. */
    this.level = clamp(num(el.getAttribute('data-liquid-level'), 22), 4, 92) / 100;
    this.amp = clamp(num(el.getAttribute('data-liquid-motion'), 100), 0, 300) / 100;

    /* Phases, so two buttons on one screen are never in step. */
    this.p1 = rand(0, 6.283);
    this.p2 = rand(0, 6.283);

    this.y = new Float32Array(COLS);
    this.v = new Float32Array(COLS);

    this.mount();
    this.measure();
    this.settle(); /* start at rest, or it pours in on load */
  }

  /**
   * Two layers and a copy of the label. The pool paints behind the real
   * label; the copy sits on top in the on-liquid colour, clipped to the same
   * surface, so a letter changes colour exactly where the liquid crosses it
   * instead of sinking into it. That is not decoration — label and liquid are
   * often close in tone, and without it the words disappear as the level
   * rises. Setting --bw-liquid-ink to the button's own text colour turns the
   * flip off without turning the pool off.
   */
  Pool.prototype.mount = function () {
    var el = this.el;

    var ghost = document.createElement('span');
    ghost.className = 'bw-liquid__ghost';
    ghost.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < el.childNodes.length; i++) {
      ghost.appendChild(el.childNodes[i].cloneNode(true));
    }

    var layer = document.createElement('span');
    layer.className = 'bw-liquid';
    layer.setAttribute('aria-hidden', 'true');

    var fill = document.createElement('span');
    fill.className = 'bw-liquid__fill';
    layer.appendChild(fill);

    /* Two droplets, reused. They are thrown by a fast upward pull and fall
       back under gravity; a pool that only ever sloshed reads as a gradient
       with a wobble rather than as a liquid. */
    this.drops = [];
    for (var d = 0; d < 2; d++) {
      var drop = document.createElement('span');
      drop.className = 'bw-liquid__drop';
      layer.appendChild(drop);
      this.drops.push({ el: drop, life: 0, x: 0, y: 0, vy: 0, r: 0 });
    }

    el.insertBefore(layer, el.firstChild);
    el.appendChild(ghost);

    this.layer = layer;
    this.fillEl = fill;
    this.ghost = ghost;
    el.classList.add('bw-liquid-on');
  };

  /**
   * Layout size, not getBoundingClientRect. The rect includes every ancestor
   * transform, and the reveal scales a button up from 70% as it enters the
   * viewport — the same moment the IntersectionObserver asks for a measure.
   * A pool sized to that shrunken rect left the fill sliced off along the
   * right and bottom, and stayed that way, because a transform settling does
   * not change the layout box and so never wakes the ResizeObserver. The
   * layer sits at inset:0 on the padding box, which is exactly what
   * clientWidth/clientHeight report, so this is also the closer match.
   */
  Pool.prototype.measure = function () {
    var w = this.el.clientWidth;
    var h = this.el.clientHeight;
    if (!w || !h) {
      var r = this.el.getBoundingClientRect();
      w = w || r.width || 1;
      h = h || r.height || 1;
    }
    if (this.h && h !== this.h) {
      /* Keep the pour where it was rather than restarting it: a resize that
         reset the surface would slam the level on every orientation change. */
      var k = h / this.h;
      for (var i = 0; i < COLS; i++) { this.y[i] *= k; this.v[i] *= k; }
    }
    this.w = w;
    this.h = h;
  };

  /**
   * The blend hero renders both blends and hides the inactive one with
   * visibility, not display — the two sets share one grid cell — so it still
   * has a box, still intersects the viewport, and would otherwise keep a pool
   * running inside a button nobody can see. visibility is the general test
   * for that, but reading a computed style is not something to do 60 times a
   * second: it is re-read only when something could actually have changed it.
   */
  Pool.prototype.recheck = function () {
    var was = this.hidden;
    this.hidden = getComputedStyle(this.el).visibility === 'hidden';
    if (this.hidden && !was) this.blank();
    return this.hidden;
  };

  /**
   * WHY A HIDDEN POOL IS WIPED RATHER THAN JUST STOPPED.
   *
   * This is the fix for the leftover button that painted itself under the
   * real one and stayed there. The sequence that produced it:
   *
   *   1. The Daily System set is the visible one. Its pool is running, and
   *      the ghost label is lit with a clip-path rewritten every frame.
   *   2. Someone clicks MOVE. data-bw-blend changes and the System set
   *      becomes visibility:hidden.
   *   3. The pool is told about that a frame later, so the render loop gets
   *      one more turn first and writes a fresh clip-path and opacity:1 onto
   *      a subtree that has just been hidden.
   *   4. Nothing in that subtree ever changes again — a stopped pool writes
   *      nothing — so whatever the compositor made of that last write is
   *      what it keeps. On a machine with GPU rasterisation that was a tile
   *      holding a picture of the System button's label, sitting 38px below
   *      the Move button because that is where the System set's button is.
   *
   * Half of the fix is that the recheck is now synchronous (see the
   * bw:blendchange handler), so step 3 cannot happen. This is the other
   * half: everything the pool has drawn is set back to empty on the way
   * out, so the last thing written into that region is nothing at all. Even
   * if a frame does get away, it has nothing in it to leave behind.
   *
   * The surface is settled too, so the blend comes back level rather than
   * resuming a ripple from however long ago it was last on screen.
   */
  Pool.prototype.blank = function () {
    var empty = 'polygon(0 100%, 100% 100%, 100% 100%, 0 100%)';
    this.fillEl.style.clipPath = empty;
    this.ghost.style.clipPath = empty;
    this.ghost.style.opacity = '0';
    this.ghostLit = false;
    this.fillT = 0;
    for (var i = 0; i < 2; i++) {
      this.drops[i].life = 0;
      this.drops[i].el.style.opacity = '0';
    }
    this.hover = false;
    this.settle();
  };

  Pool.prototype.settle = function () {
    var rest = this.h * this.level;
    for (var i = 0; i < COLS; i++) { this.y[i] = rest; this.v[i] = 0; }
  };

  /**
   * True while the surface is moving fast enough to be worth 60fps.
   *
   * Velocity, and deliberately not distance from the resting line: the drift
   * never stops, so a surface is permanently a few percent away from rest and
   * a distance test would call every button on the page busy for ever — which
   * is the throttle not existing. The threshold sits above what the drift
   * produces on its own (a few pixels a second) and well below a ripple, so
   * the tail of a settle drops to the idle rate about the point it stops
   * being visible.
   */
  Pool.prototype.busy = function () {
    var fast = this.h * 0.25;
    for (var i = 0; i < COLS; i++) {
      if (Math.abs(this.v[i]) > fast) return true;
    }
    return this.fillT > 0 || this.drops[0].life > 0 || this.drops[1].life > 0;
  };

  Pool.prototype.surge = function () {
    this.fillT = FILL_S;
    start();
  };

  Pool.prototype.step = function () {
    var h = this.h;
    var w = this.w;
    var rest = h * this.level;
    /* 0.069, up from 0.055: the pour read as heavy, especially on a phone
       where there is no hover to wake it, so the idle swell is a quarter
       larger and a quarter faster than it was. */
    var drift = h * 0.069 * this.amp;
    var i, target, d, g;

    this.t += STEP;

    /* Drift. Two travelling waves at 0.124Hz and 0.205Hz — the ratio is not a
       simple fraction, so the pair does not come back into phase within any
       time anyone spends looking at a button. */
    for (i = 0; i < COLS; i++) {
      target = rest + drift * (
        0.58 * Math.sin(this.t * 0.78 + i * 0.55 + this.p1) +
        0.42 * Math.sin(this.t * 1.29 - i * 0.31 + this.p2)
      );
      this.v[i] += (target - this.y[i]) * REST_K * STEP;
    }

    /* The press. Not a spring: a spring stiff enough to fill in a few frames
       overshoots and slaps. The surface is walked up to the brim by a fixed
       fraction of what is left each step, which is fast at first and soft at
       the top, and the spread pass below still ripples it on the way up. */
    if (this.fillT > 0) {
      this.fillT -= STEP;
      var brim = h * 0.97;
      for (i = 0; i < COLS; i++) {
        d = (brim - this.y[i]) * FILL_RATE;
        this.y[i] += d;
        this.v[i] = d / STEP * 0.35;
      }
    }

    /* The cursor. A gaussian centred on it, so the lift falls off smoothly
       rather than ending at a hard edge — an edge is what makes a pull read
       as a canned animation. Its width is a fifth of the button, roughly a
       fingertip's worth of surface. */
    if (this.hover) {
      var sigma = Math.max(w * 0.2, 26);
      var lift = clamp(h - this.py, h * 0.12, h * 0.94);
      for (i = 0; i < COLS; i++) {
        d = (i / (COLS - 1)) * w - this.px;
        g = Math.exp(-0.5 * (d * d) / (sigma * sigma));
        if (g < 0.01) continue;
        this.v[i] += (lift - this.y[i]) * g * PULL_K * STEP;
      }
    }

    /* Integrate, then let each column drag its neighbours. Spreading after
       the move is what turns 26 independent springs into one surface:
       displacement travels sideways and bounces off the ends. */
    var damp = Math.exp(-DAMP * STEP);
    for (i = 0; i < COLS; i++) {
      this.v[i] *= damp;
      this.y[i] += this.v[i] * STEP;
    }
    for (i = 0; i < COLS; i++) {
      var l = this.y[i > 0 ? i - 1 : 0];
      var r = this.y[i < COLS - 1 ? i + 1 : COLS - 1];
      this.v[i] += SPREAD * ((l - this.y[i]) + (r - this.y[i]));
      if (this.y[i] < 1) { this.y[i] = 1; this.v[i] *= -0.3; }
      if (this.y[i] > h - 1) { this.y[i] = h - 1; this.v[i] *= -0.3; }
    }

    this.stepDrops();
  };

  Pool.prototype.stepDrops = function () {
    var h = this.h;
    var w = this.w;
    var i, dr;

    /* Thrown only by a genuinely fast rise, so a slow hover stays calm. */
    if (this.hover) {
      for (i = 1; i < COLS - 1; i++) {
        if (this.v[i] < 210) continue;
        dr = this.drops[0].life <= 0 ? this.drops[0] : (this.drops[1].life <= 0 ? this.drops[1] : null);
        if (!dr) break;
        dr.x = (i / (COLS - 1)) * w + rand(-4, 4);
        dr.y = this.y[i];
        dr.vy = this.v[i] * rand(0.3, 0.5);
        dr.r = rand(2.5, 5);
        dr.life = 1;
      }
    }

    for (i = 0; i < 2; i++) {
      dr = this.drops[i];
      if (dr.life <= 0) continue;
      dr.vy -= 1400 * STEP; /* gravity, in button pixels */
      dr.y += dr.vy * STEP;
      var col = clamp(Math.round((dr.x / w) * (COLS - 1)), 0, COLS - 1);
      if (dr.y <= this.y[col] || dr.y > h * 1.4) dr.life = 0;
    }
  };

  /**
   * One polygon string, used by both the pool and the clipped label copy —
   * built once so the two can never disagree by a pixel. Coordinates run down
   * from the top of the button, which is what clip-path wants.
   */
  Pool.prototype.render = function () {
    var h = this.h;
    var w = this.w;
    var pts = '';
    var top = h;
    var i, x, y;

    for (i = 0; i < COLS; i++) {
      x = (i / (COLS - 1)) * w;
      y = h - this.y[i];
      if (y < top) top = y;
      pts += x.toFixed(1) + 'px ' + y.toFixed(1) + 'px,';
    }
    var poly = 'polygon(' + pts + w.toFixed(1) + 'px ' + h.toFixed(1) + 'px,0px ' + h.toFixed(1) + 'px)';
    this.fillEl.style.clipPath = poly;

    /* The label copy is only worth painting while the liquid is high enough
       to touch the type. Below that it is hidden — which is most of the time
       a page is sitting there drifting. 0.72 is under the deepest descender
       of a centred line, so the copy is already on by the time the waterline
       could clip a letter. */
    var lit = top < h * 0.72;
    if (lit) this.ghost.style.clipPath = poly;
    if (lit !== this.ghostLit) {
      /* OPACITY, NOT VISIBILITY.

         visibility is inherited, and a descendant is allowed to overrule an
         ancestor's hidden with its own visible. The blend hero hides the
         inactive blends with visibility — they share one grid cell — so an
         inline visibility:visible written here painted the label of a button
         nobody could see, at that blend's own position, with no button behind
         it. It stuck because a pool that has gone hidden stops stepping, so
         whatever the ghost was last set to is whatever it stays.

         opacity cannot do that: a hidden ancestor hides the subtree whatever
         its descendants ask for. */
      this.ghost.style.opacity = lit ? '1' : '0';
      this.ghostLit = lit;
    }

    for (i = 0; i < 2; i++) {
      var dr = this.drops[i];
      if (dr.life <= 0) {
        if (dr.el.style.opacity !== '0') dr.el.style.opacity = '0';
        continue;
      }
      dr.el.style.opacity = '1';
      dr.el.style.width = dr.el.style.height = (dr.r * 2).toFixed(1) + 'px';
      dr.el.style.transform = 'translate(' + (dr.x - dr.r).toFixed(1) + 'px,' +
        (h - dr.y - dr.r).toFixed(1) + 'px)';
    }
  };

  /* ---------------------------------------------------------------------- */

  function frame(now) {
    var dt = Math.min((now - last) / 1000, 0.25);
    last = now;

    if (parked) { running = false; return; }

    var anyVisible = false;

    for (var i = 0; i < pools.length; i++) {
      var p = pools[i];
      if (!p.visible || p.hidden) continue;
      anyVisible = true;

      var hot = p.hover || p.busy();
      if (!hot && now - p.painted < IDLE_MS) {
        p.acc += dt; /* keep time moving so the drift does not stall */
        continue;
      }

      p.acc += dt;
      var steps = 0;
      while (p.acc >= STEP && steps < MAX_STEPS) {
        p.step();
        p.acc -= STEP;
        steps++;
      }
      if (p.acc > STEP) p.acc = 0;
      p.render();
      p.painted = now;
    }

    if (anyVisible) {
      requestAnimationFrame(frame);
    } else {
      running = false;
    }
  }

  function start() {
    if (running || parked || warming) return;
    running = true;
    last = performance.now();
    requestAnimationFrame(frame);
  }

  /* ---------------------------------------------------------------------- */

  function bindPointer(el, pool) {
    el.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'touch') return;
      /* Map from the on-screen rect into the pool's layout pixels, so a
         button still scaling in under the reveal reads the cursor where it
         actually is on the surface. */
      var r = el.getBoundingClientRect();
      var sx = r.width ? pool.w / r.width : 1;
      var sy = r.height ? pool.h / r.height : 1;
      pool.px = (e.clientX - r.left) * sx;
      pool.py = (e.clientY - r.top) * sy;
      pool.hover = true;
    });
    el.addEventListener('pointerleave', function () { pool.hover = false; });
    el.addEventListener('pointercancel', function () { pool.hover = false; });

    /* Focus gets the same courtesy as hover, parked mid-button, so the
       keyboard path is not a dead button. */
    el.addEventListener('focus', function () {
      pool.px = pool.w * 0.5;
      pool.py = pool.h * 0.25;
      pool.hover = true;
    });
    el.addEventListener('blur', function () { pool.hover = false; });
  }

  /* Bound for every pointer type, unlike the hover above: a finger cannot
     hover, but it can press, and on a phone this is the only motion the
     button ever shows in response to the user. click covers the keyboard. */
  function bindPress(el, pool) {
    el.addEventListener('pointerdown', function () { pool.surge(); });
    el.addEventListener('click', function () { pool.surge(); });
  }

  var io = null;
  var ro = null;

  function mount() {
    var found = document.querySelectorAll('[data-bw-liquid]:not(.bw-liquid-on)');
    if (!found.length) return;

    if (!io) {
      io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var p = entries[i].target.__bwPool;
          if (!p) continue;
          p.visible = entries[i].isIntersecting;
          if (p.visible) {
            p.measure();
            p.recheck();
            start();
          }
        }
      }, { rootMargin: '80px' });

      ro = window.ResizeObserver ? new ResizeObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var p = entries[i].target.__bwPool;
          /* Same rule as the frame loop: never paint into a set that is not
             on screen. A resize observation arrives after layout, which is
             exactly when a blend switch has moved things around. */
          if (p && !p.hidden) {
            p.measure();
            p.render();
          }
        }
      }) : null;
    }

    for (var i = 0; i < found.length; i++) {
      var el = found[i];
      var pool = new Pool(el);
      el.__bwPool = pool;
      pools.push(pool);
      /* Every blend's set is in the DOM at load, two of them hidden. Painting
         all three here filled the hidden ones with a pour and a lit label
         that nothing would ever clear. */
      if (pool.recheck()) pool.blank();
      else pool.render();

      if (reduce.matches) continue; /* poured, and that is all it will do */

      io.observe(el);
      if (ro) ro.observe(el);
      bindPress(el, pool);
      if (fine.matches) bindPointer(el, pool);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }

  /* ---- ending the warm-up ------------------------------------------------ */

  /* Whichever happens first. Both paths are one-shot: settle() returns straight
     away once the flag is down, so the pointer listeners cost a single call. */

  /* Interaction. Capture phase on the document, so this runs BEFORE the
     button's own pointerdown handler calls surge() — the loop has to already be
     turning for that surge to be drawn, or the first press of the page fills
     nothing. Scroll counts too: someone reading the page is someone who should
     see it alive. */
  var WAKE = ['pointerdown', 'keydown', 'touchstart', 'wheel'];
  for (var w = 0; w < WAKE.length; w++) {
    document.addEventListener(WAKE[w], settle, { capture: true, once: true, passive: true });
  }
  window.addEventListener('scroll', settle, { once: true, passive: true });

  /* The timer. Load rather than DOMContentLoaded, because the images are the
     competition — and then an idle callback on top, so the first frame lands in
     a gap the browser has rather than on top of whatever it is still doing. The
     timeout is the backstop for a page that never goes idle, and for Safari,
     which has no requestIdleCallback. */
  function scheduleSettle() {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(settle, { timeout: 2000 });
    } else {
      setTimeout(settle, 1200);
    }
  }

  if (document.readyState === 'complete') {
    scheduleSettle();
  } else {
    window.addEventListener('load', scheduleSettle, { once: true });
  }

  /* The theme editor re-renders a section into a fresh DOM, which leaves the
     old pools pointing at detached nodes. Drop those and mount the new ones. */
  document.addEventListener('shopify:section:load', function () {
    pools = pools.filter(function (p) { return p.el.isConnected; });
    mount();
  });

  /**
   * Switching blend swaps which of the hero sets is visible.
   *
   * SYNCHRONOUS, DELIBERATELY. This used to wait a frame, on the reasoning
   * that the attribute driving the change needed time to land. It does not:
   * every dispatcher sets data-bw-blend on <html> before firing the event,
   * and the getComputedStyle inside recheck() flushes style and layout, so
   * reading it here sees the state that has just been applied.
   *
   * Waiting cost more than it bought. The render loop is a rAF callback
   * already registered, so it ran before this one did and got one last turn
   * at a pool whose set had just gone invisible — which is what left a
   * painted copy of the old button on screen. Handling it in the same turn
   * as the click means the pool is stopped and wiped before any frame after
   * the switch is produced.
   */
  document.addEventListener('bw:modal', function (e) {
    parked = !!(e.detail && e.detail.open);
    if (!parked) start();
  });

  /**
   * WAKE EVERY POOL AND ASK IT AGAIN WHETHER IT IS ON SCREEN.
   *
   * recheck() is the only thing that reads visibility, and it is deliberately
   * not read every frame. So anything that can change a button's visibility
   * has to say so, and there are two such things.
   *
   * A blend switch, which hides one set and shows another.
   *
   * And a floating panel arriving. A button inside .bw-float-switch or
   * .bw-fcta is visibility:hidden at mount, so its pool is blanked on the way
   * in and the IntersectionObserver never wakes it: the panel is fixed to the
   * bottom of the viewport, so its box is ALWAYS intersecting and the observer
   * fires exactly once, at mount, while it is still hidden. Nothing after that
   * changes intersection — only visibility — and the pool stays empty forever.
   * That is why the home page's floating reserve button was a flat black pill
   * with a correctly-coloured pour painted at zero height.
   */
  function wakeAll() {
    for (var i = 0; i < pools.length; i++) {
      var was = pools[i].hidden;
      pools[i].recheck();
      if (pools[i].hidden) continue;
      pools[i].measure();
      /* A pool that has just STOPPED being hidden is sitting on the empty clip
         blank() left behind, and blank() settles the surface on its way out —
         so the frame loop has nothing to animate and will never write over it.
         start() alone is not enough; one explicit render is what puts the
         resting pour back. */
      if (was) pools[i].render();
    }
    start();
  }

  document.addEventListener('bw:blendchange', wakeAll);

  /* A REVEAL IS TOLD, NOT MEASURED.
   *
   * recheck() answers "am I hidden" by reading computed visibility, and for a
   * floating panel that is the one thing it cannot read in time. The panels
   * transition visibility, a transition does not begin until the style recalc
   * that follows the class change, and requestAnimationFrame callbacks run
   * BEFORE that recalc — so both the synchronous read and the next-frame read
   * return the value we are trying to get away from, and the pool stays blank.
   * Waiting more frames would work by luck and break the first time the
   * transition duration changed.
   *
   * So the element that changed its own visibility says which way it went, and
   * the pools inside it are set from that rather than from the cascade. The
   * dispatcher is the authority on a state it has just written.
   *
   * A reveal with no panel in it is a general poke and falls back to reading,
   * which is correct for anything that is not mid-transition.
   */
  document.addEventListener('bw:reveal', function (e) {
    var d = e && e.detail;
    if (!d || !d.panel) { wakeAll(); return; }

    for (var i = 0; i < pools.length; i++) {
      var p = pools[i];
      if (!d.panel.contains(p.el)) continue;
      var was = p.hidden;
      if (d.shown) {
        p.hidden = false;
        p.measure();
        /* Sitting on the empty clip blank() left behind, with a settled
           surface — so the frame loop has nothing to animate and would never
           write over it. One render is what puts the resting pour back. */
        if (was) p.render();
      } else if (!was) {
        p.hidden = true;
        p.blank();
      }
    }
    start();
  });
}());
